import React, { useMemo, useState } from 'react';
import classNames from 'classnames';
import { SlideoutMenu } from '../../components/SlideoutMenu';

import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import CompanyModel from '@mm/state/model/CompanyModel';
import css from './SlideoutBusinessForm.module.css';
import { AngleLeftLightIcon } from '@mm/icons';
import { useViewActions } from '@mm/state/actions/viewActions';
import { Heading } from '@mm/components';
import RadioInput from '../../../../../common/components/RadioInput';
import Input from '../../../../../common/components/Input/Input';
import ImageUploader from '../../../../../common/components/ImageUploader/ImageUploader';
import { FacebookLoginAndConnectButton } from '../../../../../modules/permissions/ConnectPageWarning/ConnectPageWarning';
import { URL_REGEX } from '../../../../../common/constants/validations';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import Spinner from '../../../../../common/components/Spinner';
import { useResolutionInfo } from '@mm/hooks';
import { MOBILE_THRESHOLD } from '../../../../constants/screenSize';
import { Dispatch } from 'redux';
import * as allPermissions from '@mm/constants/facebook-permissions';
import { ErrorReportingUtils } from '@mm/plugins';
import facebookSDKActions from '@mm/state/actions/facebookActions';
import { useFacebookSessionCRUDActions } from '@mm/state/actions/FacebookSessionCRUDActions';
import { useDispatch, useSelector } from 'react-redux';
import { trackWebAppGAEvent } from '../../../../../common/helpers/plugins/website-google-analytics';
import { StoreState } from '@mm/state/store-interfaces';
import {
  isFacebookConnectedSelector,
  isFacebookUserSelector,
} from '@mm/state/selectors/userSelector';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { activeCompanySelector } from '@mm/state/selectors/viewSelectors';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';

export const useFacebookLogin = () => {
  const dispatch: Dispatch<StoreState> = useDispatch();

  const permissions = allPermissions.getArrayOfPermissions(
    allPermissions.ALL_PERMISSIONS,
  );

  const login = async () => {
    try {
      const res = await dispatch(
        facebookSDKActions.requestPermissionsWithManualFlowFallback(
          permissions,
        ),
      );
      if (res) {
        trackWebAppGAEvent('login_before_connect_page', {
          category: 'login',
          label: 'login_success',
        });

        return res;
      }
    } catch (error) {
      ErrorReportingUtils.reportError(`Active Page Slide Menu: login failure`, {
        extraData: { permissionsRequested: permissions, error },
      });
      trackWebAppGAEvent('login_before_connect_page', {
        category: 'login',
        label: 'login_fail',
      });
      throw error;
    }
  };

  return { login };
};

type Props = {
  className?: string;
  company?: CompanyModel;
};

type Mode = 'website' | 'facebook';

export const SlideoutBusinessForm = ({ company, className }: Props) => {
  const viewActions = useViewActions();
  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= MOBILE_THRESHOLD;

  const isFacebookConnected = useSelector(isFacebookConnectedSelector);
  const modalActions = useModalActions();
  const activeCompany = useSelector(activeCompanySelector);
  const facebookSessionCRUDActions = useFacebookSessionCRUDActions();
  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const isFacebookUser = useSelector(isFacebookUserSelector);

  const { login } = useFacebookLogin();

  const navigateToPagesList = () => {
    if (isMobile) {
      viewActions.hideSlideoutMenu();
    } else {
      viewActions.showSlideoutMenu('pages');
    }
  };

  const [mode, setMode] = useState<Mode>('website');
  const [image, setImage] = useState<{ url?: string; assetId?: number }>({
    url: undefined,
    assetId: undefined,
  });
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);

  const onUrlChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setUrl(e.target.value);

  const onNameChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setName(e.target.value);

  const onRadioClick = (mode: Mode) => {
    setMode(mode);
  };

  const onImageUpload = (assetId: number, url: string) =>
    setImage({ assetId, url });

  const onImageRemove = () => setImage({});

  const onCancelBtnClick = navigateToPagesList;

  const pageCRUDActions = useConnectedPageCRUDActions();
  const onConnectBtnClick = async () => {
    setBusy(true);
    if (company) {
      const params = {
        name,
        businessWebsite: url,
        businessAssetId: image.assetId,
        business: true,
      };

      const res = await pageCRUDActions.createBusinessPage(company.id, params);

      if (res?.success) {
        navigateToPagesList();
      }
    }
    setBusy(false);
  };

  const isURLValid = useMemo(() => URL_REGEX.test(url), [url]);
  const isNameEmpty = /^(\s+$)/g.test(name);

  const isConnectBtnDisabled = useMemo(
    () => !name.length || isNameEmpty || !url.length || !isURLValid || busy,
    [url, isURLValid, name, image, busy],
  );

  const isFacebook = mode === 'facebook';
  const isWebsite = mode === 'website';

  const websiteFormatted = activeCompany?.website;

  const onConnectPageClick = async () => {
    if (!isFacebookConnected) {
      const session = await login();
      if (session && activeCompany) {
        await facebookSessionCRUDActions.create(session);
        return modalActions.openConnectPagesModal({
          title: `Connect Facebook Page to ${
            websiteFormatted || 'your website'
          }`,
          company: activeCompany,
          destinationPageId: activeFacebookPage?.id,
        });
      }
    }

    if (activeCompany) {
      modalActions.openConnectPagesModal({ company: activeCompany });
    }
  };

  return (
    <SlideoutMenu className={classNames(className, css.container)}>
      <SlideoutMenu.Header onClose={viewActions.hideSlideoutMenu}>
        <div className={css.back} onClick={navigateToPagesList}>
          <AngleLeftLightIcon />
          Back to Business List
        </div>
      </SlideoutMenu.Header>
      <SlideoutMenu.Content className={css.content}>
        {busy && <Spinner show standalone={true} />}

        {!busy && (
          <>
            <Heading size={4} className={css.heading}>
              Connect business to {company?.name}
            </Heading>

            <div className={css.radios}>
              <RadioInput
                className={css.radio}
                value="facebook"
                checked={isFacebook}
                name="Facebook Page"
                label="Facebook Page"
                onClick={() => onRadioClick('facebook')}
              />

              <RadioInput
                className={css.radio}
                value="website"
                checked={isWebsite}
                name="Website"
                label="Website"
                onClick={() => onRadioClick('website')}
              />
            </div>

            {isFacebook && (
              <div>
                {isFacebookUser ? (
                  <PrimaryButton
                    className="facebook-login-button"
                    onClick={() => onConnectPageClick()}
                  >
                    Connect Facebook Pages
                  </PrimaryButton>
                ) : (
                  <FacebookLoginAndConnectButton
                    onPageConnectSuccess={navigateToPagesList}
                    showIconWarning={false}
                  />
                )}
              </div>
            )}

            {isWebsite && (
              <>
                <div className={css.formField}>
                  <Heading size={4} isBold={false}>
                    Business Name *
                  </Heading>
                  <Input
                    className={classNames(css.input, {
                      [css.warn]: isNameEmpty && name.length,
                    })}
                    placeholder="Customers.ai"
                    value={name}
                    onChange={onNameChange}
                  />
                </div>

                <div className={css.formField}>
                  <Heading size={4} isBold={false}>
                    Enter a website URL *
                  </Heading>
                  <Input
                    className={classNames(css.input, {
                      [css.warn]: !isURLValid && url.length,
                    })}
                    placeholder="https://customers.ai"
                    value={url}
                    onChange={onUrlChange}
                  />
                </div>

                <div className={css.formField}>
                  <Heading size={4} isBold={false}>
                    Business Logo
                  </Heading>

                  <ImageUploader
                    className={css.uploader}
                    css={{ dropArea: css.dropArea }}
                    file={image.url || null}
                    onUpload={onImageUpload}
                    onRemove={onImageRemove}
                    disableCrop={true}
                    withRemoveBtn={true}
                  />
                </div>
              </>
            )}
          </>
        )}
      </SlideoutMenu.Content>
      <div className={css.footer}>
        <PrimaryButton
          className={css.btn}
          color={ButtonColor.secondaryBlue}
          disabled={busy}
          onClick={onCancelBtnClick}
        >
          Cancel
        </PrimaryButton>

        {mode === 'website' && (
          <PrimaryButton
            disabled={isConnectBtnDisabled}
            onClick={onConnectBtnClick}
          >
            Connect
          </PrimaryButton>
        )}
      </div>
    </SlideoutMenu>
  );
};
